/**
 * LLM Provider definitions with display names and Simple Icons slugs
 */
export const LLM_PROVIDERS = {
  openai: { name: "OpenAI", slug: "openai", color: "#412991" },
  anthropic: { name: "Anthropic", slug: "anthropic", color: "#D4A27F" },
  google: { name: "Google", slug: "google", color: "#4285F4" },
  meta: { name: "Meta", slug: "meta", color: "#0082FB" },
  mistral: { name: "Mistral AI", slug: "mistral", color: "#F7D046" },
  xai: { name: "xAI", slug: "x", color: "#000000" },
  alibaba: { name: "Alibaba", slug: "alibabacloud", color: "#FF6A00" },
  cohere: { name: "Cohere", slug: "cohere", color: "#39594D" },
  deepseek: { name: "DeepSeek", slug: "deepseek", color: "#0066FF" },
  cursor: { name: "Cursor", slug: "cursor", color: "#000000" },
} as const;

export type LLMProvider = keyof typeof LLM_PROVIDERS;

/**
 * Canonical list of LLM models for Top 3 rankings
 * This list should be kept in sync with the database seed data
 */
export const LLM_MODELS = [
  // OpenAI
  "GPT-5",
  "GPT-5.1",
  "GPT-5.1 Codex",
  "GPT-5 Chat",
  "GPT-5 Codex",
  "GPT-5 mini",
  "GPT-5 nano",
  "GPT-5 pro",
  "GPT-4.1",
  "GPT-4o",
  "GPT-4 Turbo",
  "GPT-4",
  "gpt-oss-120b",
  "gpt-oss-20b",
  "o3",
  "o1",
  "Sora 2",
  "Sora 2 Pro",
  // Anthropic
  "Claude Opus 4.5",
  "Claude Opus 4.1",
  "Claude Opus 4",
  "Claude Sonnet 4.5",
  "Claude Sonnet 4",
  "Claude Haiku 4.5",
  "Claude 3.5 Sonnet",
  "Claude 3 Opus",
  "Claude 3 Sonnet",
  "Claude 3 Haiku",
  // Google
  "Gemini 3 Pro",
  "Gemini 2.5 Pro",
  "Gemini 2.0 Flash",
  "Gemini 1.5 Pro",
  "Gemini Ultra",
  "Nano Banana",
  "Nano Banana Pro",
  // Meta
  "Llama 4 405B",
  "Llama 4 70B",
  "Llama 3.3 70B",
  "Llama 3.1 405B",
  // Mistral
  "Mistral Large",
  "Mistral Medium",
  "Mixtral 8x22B",
  "Pixtral",
  "Ministral3 14B",
  "Ministral3 8B",
  "Ministral3 3B",
  // xAI
  "Grok 3",
  "Grok 2",
  // Others
  "Qwen 2.5",
  "Command R+",
  "DeepSeek V3",
  "Composer 1",
] as const;

export type LLMModel = (typeof LLM_MODELS)[number];

/**
 * Model metadata mapping each model to its provider
 */
export const MODEL_METADATA: Record<LLMModel, { provider: LLMProvider }> = {
  // OpenAI
  "GPT-5": { provider: "openai" },
  "GPT-5.1": { provider: "openai" },
  "GPT-5.1 Codex": { provider: "openai" },
  "GPT-5 Chat": { provider: "openai" },
  "GPT-5 Codex": { provider: "openai" },
  "GPT-5 mini": { provider: "openai" },
  "GPT-5 nano": { provider: "openai" },
  "GPT-5 pro": { provider: "openai" },
  "GPT-4.1": { provider: "openai" },
  "GPT-4o": { provider: "openai" },
  "GPT-4 Turbo": { provider: "openai" },
  "GPT-4": { provider: "openai" },
  "gpt-oss-120b": { provider: "openai" },
  "gpt-oss-20b": { provider: "openai" },
  "o3": { provider: "openai" },
  "o1": { provider: "openai" },
  "Sora 2": { provider: "openai" },
  "Sora 2 Pro": { provider: "openai" },
  // Anthropic
  "Claude Opus 4.5": { provider: "anthropic" },
  "Claude Opus 4.1": { provider: "anthropic" },
  "Claude Opus 4": { provider: "anthropic" },
  "Claude Sonnet 4.5": { provider: "anthropic" },
  "Claude Sonnet 4": { provider: "anthropic" },
  "Claude Haiku 4.5": { provider: "anthropic" },
  "Claude 3.5 Sonnet": { provider: "anthropic" },
  "Claude 3 Opus": { provider: "anthropic" },
  "Claude 3 Sonnet": { provider: "anthropic" },
  "Claude 3 Haiku": { provider: "anthropic" },
  // Google
  "Gemini 3 Pro": { provider: "google" },
  "Gemini 2.5 Pro": { provider: "google" },
  "Gemini 2.0 Flash": { provider: "google" },
  "Gemini 1.5 Pro": { provider: "google" },
  "Gemini Ultra": { provider: "google" },
  "Nano Banana": { provider: "google" },
  "Nano Banana Pro": { provider: "google" },
  // Meta
  "Llama 4 405B": { provider: "meta" },
  "Llama 4 70B": { provider: "meta" },
  "Llama 3.3 70B": { provider: "meta" },
  "Llama 3.1 405B": { provider: "meta" },
  // Mistral
  "Mistral Large": { provider: "mistral" },
  "Mistral Medium": { provider: "mistral" },
  "Mixtral 8x22B": { provider: "mistral" },
  "Pixtral": { provider: "mistral" },
  "Ministral3 14B": { provider: "mistral" },
  "Ministral3 8B": { provider: "mistral" },
  "Ministral3 3B": { provider: "mistral" },
  // xAI
  "Grok 3": { provider: "xai" },
  "Grok 2": { provider: "xai" },
  // Others
  "Qwen 2.5": { provider: "alibaba" },
  "Command R+": { provider: "cohere" },
  "DeepSeek V3": { provider: "deepseek" },
  "Composer 1": { provider: "cursor" },
};

/**
 * Get models grouped by provider
 */
export function getModelsByProvider(): Record<LLMProvider, LLMModel[]> {
  const grouped = {} as Record<LLMProvider, LLMModel[]>;

  for (const model of LLM_MODELS) {
    const { provider } = MODEL_METADATA[model];
    if (!grouped[provider]) {
      grouped[provider] = [];
    }
    grouped[provider].push(model);
  }

  return grouped;
}

